import { useEffect, useRef } from 'react';

/**
 * Generic modal dialog. The caller controls open/close via `open` and
 * `onClose`. Pressing Escape, clicking the backdrop, or clicking the close
 * button all fire `onClose()`. Focus moves to the first focusable child on
 * open and returns to the previously-focused element on close.
 *
 * Render the action buttons (cancel + confirm) inside `children`; this
 * component is layout-only.
 */
export function Modal({ open, onClose, title, children, labelledBy, describedBy }) {
  const dialogRef = useRef(null);
  const previousActiveRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    previousActiveRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    const root = dialogRef.current;
    if (root) {
      const focusable = root.querySelector(
        'input, textarea, button:not([disabled]), [href], select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable && typeof focusable.focus === 'function') focusable.focus();
    }
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const prev = previousActiveRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={onBackdrop}
      data-testid="modal-backdrop"
    >
      <div
        ref={dialogRef}
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
      >
        {title && (
          <div className="modal-header">
            <h3 id={labelledBy} className="modal-title">{title}</h3>
            <button
              type="button"
              className="modal-close"
              onClick={onClose}
              aria-label="Close dialog"
            >
              ×
            </button>
          </div>
        )}
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}
